import { Cart } from 'types/entity/cart';
import { UseCartReturn } from 'frontastic/hooks/useCart/types';
import { EU_LOCALE, NORTH_AMERICA_LOCALES, US_LOCALE } from 'project.config';

type MapCosts = (props: {
  cart?: Partial<Cart>;
  currency?: string;
  countryCode?: string;
  enableCartDiscountChanges?: boolean;
  isFreeAbove?: boolean;
}) => UseCartReturn['transaction'];

const emptyMoney = (currencyCode: string, fractionDigits = 2) => ({
  centAmount: 0,
  currencyCode,
  fractionDigits,
});

const getRegion = (countryCode: string) => (NORTH_AMERICA_LOCALES.includes(countryCode) ? countryCode : EU_LOCALE);

const calculateSubtotal = (items: Cart['lineItems'], isEU: boolean, enableCartDiscountChanges: boolean) =>
  (items ?? []).reduce((acc, item) => {
    const base = enableCartDiscountChanges
      ? (item.originalTotalPrice?.centAmount ?? 0)
      : (item.originalSalePrice?.centAmount ?? 0);
    const taxAdjustment = !isEU && item.taxRate?.includedInPrice ? (item.taxed?.taxAmount?.centAmount ?? 0) : 0;

    return acc + base - taxAdjustment;
  }, 0);

const calculateDiscount = (cart: Partial<Cart>) =>
  (cart.discountOnTotalPrice?.discountedAmount?.centAmount ?? 0) +
  (cart.lineItems ?? []).reduce((acc, item) => {
    if (item.isGift) return acc;

    const original = (item.price?.centAmount ?? 0) * (item.count as number);
    const discounted = item.totalPrice?.centAmount ?? 0;

    return acc + (original - discounted);
  }, 0);

const calculateExcludedTaxes = (cart: Partial<Cart>) => {
  const lineItemTaxes = (cart.lineItems ?? [])
    .filter((item) => !item.taxRate?.includedInPrice)
    .reduce((acc, item) => acc + (item.taxed?.taxAmount?.centAmount ?? 0), 0);

  const shippingTaxes = cart.shippingInfo?.taxRate?.includedInPrice
    ? 0
    : (cart.shippingInfo?.taxed?.taxAmount?.centAmount ?? 0);

  return lineItemTaxes + shippingTaxes;
};

const calculateShipping = (cart: Partial<Cart>, totalAmount: number) => {
  const shipping = cart.shippingInfo;
  if (!shipping || totalAmount <= 0) return 0;

  return (
    shipping.discountedPrice?.value.centAmount ?? shipping.price?.centAmount ?? shipping.rate?.price?.centAmount ?? 0
  );
};

const mapCosts: MapCosts = ({
  cart,
  currency = 'USD',
  countryCode = US_LOCALE,
  enableCartDiscountChanges = false,
  isFreeAbove = false,
}) => {
  const currencyCode = cart?.sum?.currencyCode ?? currency;
  const fractionDigits = cart?.sum?.fractionDigits ?? 2;

  if (!cart) {
    return {
      subtotal: emptyMoney(currencyCode, fractionDigits),
      discount: emptyMoney(currencyCode, fractionDigits),
      tax: emptyMoney(currencyCode, fractionDigits),
      shipping: emptyMoney(currencyCode, fractionDigits),
      isShippingDiscounted: false,
      isShippingCalculated: false,
      addAmountForFreeShipping: emptyMoney(currencyCode, fractionDigits),
      total: emptyMoney(currencyCode, fractionDigits),
      totalSavings: emptyMoney(currencyCode, fractionDigits),
      couponDiscount: emptyMoney(currencyCode, fractionDigits),
    };
  }

  const region = getRegion(countryCode);
  const isEU = region === EU_LOCALE;

  const totalAmount = cart.sum?.centAmount ?? 0;
  const subtotal = calculateSubtotal(cart.lineItems, isEU, enableCartDiscountChanges);
  const discount = calculateDiscount(cart);
  const excludedTaxes = calculateExcludedTaxes(cart);
  const shipping = calculateShipping(cart, totalAmount);

  const isEstimatedShipping = !cart.shippingInfo;

  const isShippingDiscounted =
    cart.shippingInfo?.rate?.price?.centAmount !== undefined &&
    (cart.shippingInfo.discountedPrice?.value.centAmount ?? cart.shippingInfo.price?.centAmount ?? Infinity) <
      cart.shippingInfo.rate.price.centAmount;

  const isShippingCalculated =
    !!cart.shippingInfo &&
    cart.shippingInfo?.rate?.price?.centAmount !== undefined &&
    cart.shippingInfo.price?.centAmount !== undefined;

  const baseFreeAbove =
    typeof cart?.shippingInfo?.freeAbove === 'number'
      ? {
          centAmount: cart?.shippingInfo?.freeAbove ?? 0,
          currencyCode,
          fractionDigits,
        }
      : cart?.shippingInfo?.freeAbove;
  const freeAbove = isFreeAbove ? baseFreeAbove : cart.shippingInfo?.rate?.freeAbove;
  const freeAboveCentAmount = freeAbove?.centAmount ?? 0;

  const addAmountForFreeShipping = freeAbove
    ? {
        ...freeAbove,
        centAmount:
          shipping === 0 ? 0 : freeAboveCentAmount - subtotal + (cart?.allDiscounts?.totalDiscount?.centAmount ?? 0),
      }
    : undefined;

  const lineItemTaxed = cart?.lineItems?.reduce((prev, cur) => (cur?.taxed?.taxAmount?.centAmount ?? 0) + prev, 0) ?? 0;

  const shipTax =
    cart?.shippingInfo?.price?.centAmount && cart?.shippingInfo?.price?.centAmount > 0
      ? cart.shippingInfo?.taxed?.taxAmount?.centAmount
      : 0;

  const totalTax = totalAmount > 0 ? lineItemTaxed + (shipTax ?? 0) : 0;

  return {
    subtotal: { centAmount: subtotal, currencyCode, fractionDigits },
    discount: { centAmount: discount, currencyCode, fractionDigits },
    shipping: { centAmount: shipping, currencyCode, fractionDigits },
    isShippingDiscounted,
    isEstimatedShipping,
    isShippingCalculated,
    addAmountForFreeShipping,
    tax: {
      centAmount: totalTax,
      currencyCode,
      fractionDigits,
    },
    total: {
      centAmount: totalAmount + (isEstimatedShipping ? shipping : 0) + excludedTaxes,
      currencyCode,
      fractionDigits,
    },
    totalTaxRate: totalAmount > 0 ? (cart.shippingInfo?.taxRate?.amount ?? 0) : 0,
    totalSavings: {
      centAmount: cart.totalSavings?.centAmount ?? 0,
      currencyCode,
      fractionDigits,
    },
    couponDiscount: {
      centAmount: cart.totalDiscount?.centAmount ?? 0,
      currencyCode,
      fractionDigits,
    },
    allDiscounts: cart.allDiscounts,
  };
};

export default mapCosts;
